import { Injectable } from '@angular/core';
import { Action, Actions, Selector, State, StateContext, Store, ofActionSuccessful } from '@ngxs/store';
import _ from 'lodash';
import { debounceTime, filter, switchMap } from 'rxjs/operators';
import { DirectorySettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/directory-settings/directory-settings.state';
import { DirectoryColumnTypesEntryRemoteDto } from 'src/generated/signalR/directory-column-types-entry-remote-dto';
import { DirectoryEntryRemoteDto } from 'src/generated/swagger';
import { DirectoryBrowserEntrySelected } from '../directory-behaviour/directory-browser-behaviour/directory-browser-behaviour.actions';
import { DirectoryDataBehaviourState } from '../directory-behaviour/directory-data/directory-data-behaviour.state';
import { DirectoryColumnTypes, DirectoryHelperEntrySelected } from './directory-helper.actions';

export interface DirectoryHelperStateModel {
   directoryEntryId: number;
   directoryBrickInstanceId: string;
   directoryColumnTypes: DirectoryColumnTypesEntryRemoteDto[];
}

@State<DirectoryHelperStateModel>({
   name: 'directoryHelper',
   defaults: {
      directoryEntryId: 0,
      directoryBrickInstanceId: null,
      directoryColumnTypes: [],
   },
})
@Injectable()
export class DirectoryHelperState {
   constructor(private store: Store, private actions$: Actions) {
      // The load caused by directory grids which are reloading their data can cause a wrong entry to be called when
      // selecting rows by clicking with the mouse and then using the shortcut key to initiate a call.
      // In this scenario the key event is triggered before the new entry is actually selected.
      // Therefore we debounce the selection change events to reduce the load caused by directory bricks which automatically
      // trigger a search based on the selection change event of another grid (auto-search feature).
      // This reduces the chance of the aforementioned issue. Additionally this reduces unnecessary calls to the backend.
      this.actions$
         .pipe(
            ofActionSuccessful(DirectoryBrowserEntrySelected),
            filter((action: DirectoryBrowserEntrySelected) =>
               _.isNil(this.store.selectSnapshot(DirectorySettingsState.getDefaultListeningColumn)(action.brickInstanceId))
            ),
            debounceTime(750),
            filter((action: DirectoryBrowserEntrySelected) => this.store.selectSnapshot(DirectoryHelperState.getSelectedEntryID) !== action.directoryId),
            switchMap((action: DirectoryBrowserEntrySelected) =>
               this.store.dispatch(new DirectoryHelperEntrySelected(action.brickInstanceId, action.directoryId))
            )
         )
         .subscribe();
   }

   @Selector([DirectoryHelperState])
   public static getSelectedBrickInstanceId(state: DirectoryHelperStateModel): null | string {
      return state.directoryBrickInstanceId;
   }

   @Selector([DirectoryHelperState.getSelectedBrickInstanceId, DirectoryHelperState.getSelectedEntryID, DirectoryDataBehaviourState.getDirectoryEntryById])
   public static getSelectedEntryForSearch(
      brickInstanceId: string,
      selectedEntry: number,
      getSelectedEntry: (brickInstanceId: string, orgId: number) => DirectoryEntryRemoteDto
   ) {
      return getSelectedEntry(brickInstanceId, selectedEntry);
   }

   @Selector([DirectoryHelperState])
   public static getSelectedEntryID(state: DirectoryHelperStateModel): null | number {
      return state.directoryEntryId;
   }

   @Selector([DirectoryHelperState])
   public static directoryColumnTypes(state: DirectoryHelperStateModel): DirectoryColumnTypesEntryRemoteDto[] {
      return state.directoryColumnTypes;
   }

   @Selector([DirectoryHelperState.directoryColumnTypes])
   public static customColumnsMarkedAsEmail(directoryColumnTypes: DirectoryColumnTypesEntryRemoteDto[]): DirectoryColumnTypesEntryRemoteDto[] {
      return directoryColumnTypes.filter((entry) => entry.fldIsEmail);
   }

   @Selector([DirectoryHelperState.customColumnsMarkedAsEmail])
   public static additionalEmailColumns(directoryColumnTypes: DirectoryColumnTypesEntryRemoteDto[]): DirectoryColumnTypesEntryRemoteDto[] {
      return directoryColumnTypes.filter((entry) => !entry.fldColumnName.includes('fld_email'));
   }

   @Action(DirectoryHelperEntrySelected)
   public directoryHelperEntrySelected(ctx: StateContext<DirectoryHelperStateModel>, action: DirectoryHelperEntrySelected) {
      ctx.patchState({
         directoryEntryId: action.directoryEntryId,
         directoryBrickInstanceId: action.brickInstanceId,
      });
   }

   @Action(DirectoryColumnTypes)
   public directoryColumnTypes(ctx: StateContext<DirectoryHelperStateModel>, action: DirectoryColumnTypes) {
      ctx.patchState({
         directoryColumnTypes: action.directoryColumns.directoryColumnTypes.directoryColumnTypesList,
      });
   }
}
